import { useCallback, useRef, useState } from "react";
import { CheckCircle2, CloudUpload, FileText, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatBytes } from "@/lib/utils";

type UploadItem = {
  id: string;
  file: File;
  status: "queued" | "uploading" | "done" | "error";
  documentId?: string;
  error?: string;
};

export function UploadPage() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const enqueue = useCallback((files: FileList | File[]) => {
    const next: UploadItem[] = Array.from(files).map((file) => ({
      id: crypto.randomUUID(),
      file,
      status: "queued",
    }));
    setItems((prev) => [...prev, ...next]);
    void Promise.all(next.map(uploadOne));
  }, []);

  async function uploadOne(item: UploadItem) {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "uploading" } : i)));
    try {
      const { documentId } = await api.uploadDocument(item.file);
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "done", documentId } : i)),
      );
    } catch (err) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, status: "error", error: (err as Error).message } : i,
        ),
      );
    }
  }

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col gap-4 p-4">
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length) enqueue(e.dataTransfer.files);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 text-center transition ${
          dragging ? "border-primary bg-accent" : "border-border bg-muted/30"
        }`}
      >
        <CloudUpload className="h-10 w-10 text-muted-foreground" />
        <div className="text-sm font-medium">Drop a file or tap to choose</div>
        <div className="text-xs text-muted-foreground">PDF, JPG, PNG, HEIC, .eml, .csv, .xlsx</div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple
          onChange={(e) => {
            if (e.target.files?.length) enqueue(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            inputRef.current?.click();
          }}
        >
          Choose files
        </Button>
      </label>

      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <UploadRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function UploadRow({ item }: { item: UploadItem }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
      <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{item.file.name}</div>
        <div className="text-xs text-muted-foreground">
          {formatBytes(item.file.size)} · {item.status}
          {item.error ? ` — ${item.error}` : ""}
        </div>
      </div>
      <StatusIcon status={item.status} />
    </div>
  );
}

function StatusIcon({ status }: { status: UploadItem["status"] }) {
  if (status === "uploading") return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  if (status === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "error") return <XCircle className="h-4 w-4 text-destructive" />;
  return null;
}
